import {Experience} from '@wix/ambassador-table-reservations-v1-experience/types'
import {ReservationLocation} from '@wix/ambassador-table-reservations-v1-reservation-location/types'

import {isExperienceInPast} from './experienceStatus'
import {sortExperiencesAlphabetically, sortExperiencesManually} from './sortExperiences'
import {
  ExperienceSortOrder,
  ExperiencesDisplayMode,
  ExperienceStatusFilter,
  ExperiencesFilterConfig,
} from './experiencesFilterConfig'

const applyStatusFilter = (
  experiences: Experience[],
  statusFilter: ExperienceStatusFilter,
): Experience[] => {
  if (statusFilter === ExperienceStatusFilter.UpcomingAndPast) {
    return experiences
  }

  return experiences.filter((experience) => {
    const isPast = isExperienceInPast(experience)

    return statusFilter === ExperienceStatusFilter.Past ? isPast : !isPast
  })
}

export const filterReservationLocationsAndExperiences = (
  reservationLocations: ReservationLocation[],
  experiences: Experience[],
  filterConfig: ExperiencesFilterConfig,
  isEditorOrPreview: boolean,
  applySorting = true,
) => {
  const unarchivedReservationLocations = reservationLocations.filter(({archived}) => !archived)
  const visibleExperiences = experiences.filter(({configuration}) => configuration?.visible)

  const {displayMode, statusFilter, sortOrder, manualOrder} = filterConfig

  const enabledLocationIds =
    displayMode === ExperiencesDisplayMode.ByLocation ? filterConfig.enabledLocationIds : undefined
  const enabledExperienceIds =
    displayMode === ExperiencesDisplayMode.Custom ? filterConfig.enabledExperienceIds : undefined

  const locationIdSet = enabledLocationIds ? new Set(enabledLocationIds) : undefined
  const experienceIdSet = enabledExperienceIds ? new Set(enabledExperienceIds) : undefined

  const reservationLocationsMap: Record<string, ReservationLocation> = {}

  let filteredExperiences = visibleExperiences.filter((experience) => {
    const {reservationLocationId} = experience

    if (experienceIdSet && !experienceIdSet.has(experience.id!)) {
      return false
    }

    if (!reservationLocationId) {
      return isEditorOrPreview && !locationIdSet
    }

    const experienceReservationLocation = unarchivedReservationLocations.find(
      ({id}) => id === reservationLocationId,
    )

    if (!experienceReservationLocation) {
      return false
    }

    if (locationIdSet && !locationIdSet.has(experienceReservationLocation.id!)) {
      return false
    }

    reservationLocationsMap[experienceReservationLocation.id!] = experienceReservationLocation

    return true
  })

  filteredExperiences = applyStatusFilter(filteredExperiences, statusFilter)

  if (applySorting) {
    if (
      displayMode === ExperiencesDisplayMode.Custom &&
      sortOrder === ExperienceSortOrder.Manually
    ) {
      filteredExperiences = sortExperiencesManually(filteredExperiences, manualOrder)
    } else {
      filteredExperiences = sortExperiencesAlphabetically(filteredExperiences)
    }
  }

  return {
    reservationLocationsMap,
    filteredExperiences,
  }
}
